import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import AutoSizer from 'components/AutoSizer';
import {BenchlingState} from 'redux-stores/reduceApp';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';
import {StageRunGraph} from 'workflows/visualization/TaggedNodeGraph';
import {VirtualNodeGraph} from 'workflows/visualization/VirtualNodeGraph';
import VirtualNodeGraphVisualization from 'workflows/visualization/VirtualNodeGraphVisualization';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageConfiguration,
  StageEntryDocument,
  StageEntrySummary,
  StageRunSample,
  WORKFLOW_STAGE_FULL_SHAPE,
  WorkflowStage,
} from 'workflows/WorkflowConstants';

import ConfigureStageRunHeader from './ConfigureStageRunHeader';
import ConfigureStageRunRow from './ConfigureStageRunRow';

interface EnhancedProps {
  stageConfigurations: Array<StageConfiguration>;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  workflowStages: Array<WorkflowStage> | undefined;
}

function mapStateToProps(
  state: BenchlingState,
  {workflowId}: BaseConfigureStageRunsModalProps
): EnhancedProps {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const workflow = workflowSelector.getWorkflow(workflowId)!;
  return {
    stageConfigurations: workflowSelector.getStageConfigurations(workflow.workflowTemplateVersion.id)!,
    stageRunSampleById: workflowSelector.getStageRunSampleById(),
    workflowStages: workflowSelector.getWorkflowStages(workflowId),
  };
}

type ParentSpecifier = {clientStageRunId: string} | {existingStageRunId: string};

export interface CreateSpecifier {
  clientStageRunId: string;
  inputSampleSpecifiers: Array<{stageRunSampleId: string}>;
  workflowStageId: string;
  stageEntryId: string | null;
  experimentalConditionValueIds: Array<string>;
  parentSpecifiers: Array<ParentSpecifier>;
}

type SetParentPatch = {stageRunId: string; parentSpecifiers: Array<ParentSpecifier>} | {};
type SetRowPatch =
  | {
      stageRunId: string;
      name: string | undefined;
      inputSampleSpecifiers: Array<{stageRunSampleId: string}>;
      stageEntryId: string | null;
    }
  | {};

export type EditSpecifier = SetParentPatch | SetRowPatch | (SetParentPatch & SetRowPatch);

export interface BaseConfigureStageRunsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (numChangedRuns: number) => void;
  nodeIdsToEdit: Array<string>;
  nodeIdToSetParent?: string;
  nodeIdToInputSampleIds?: {[nodeId: string]: Array<string>};
  virtualNodeGraph: VirtualNodeGraph<StageRunGraph>;
  workflowId: string;
}

interface RowsByNodeId {
  [nodeId: string]: {stageEntry: StageEntrySummary | null; inputSampleIds: Array<string>; name?: string};
}

interface State {
  rowsByNodeId: RowsByNodeId;
  virtualNodeGraph: VirtualNodeGraph<StageRunGraph>;
}

const INPUT_SAMPLE_IDS_SHAPE = PropTypes.arrayOf(PropTypes.string.isRequired);

class ConfigureStageRunsModal extends React.Component<
  BaseConfigureStageRunsModalProps & EnhancedProps,
  State
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    onRequestClose: PropTypes.func.isRequired,
    // Existing nodes currently being edited in the modal
    nodeIdsToEdit: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    // The node ID for which we are currently choosing the parent.
    // This will eventually be a list, and added to component state as well.
    nodeIdToSetParent: PropTypes.string,
    nodeIdToInputSampleIds: PropTypes.objectOf(INPUT_SAMPLE_IDS_SHAPE.isRequired),
    virtualNodeGraph: PropTypes.object.isRequired,
    workflowId: PropTypes.string.isRequired,

    // Props provided by connect
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
    workflowStages: PropTypes.arrayOf(WORKFLOW_STAGE_FULL_SHAPE.isRequired).isRequired,
  };

  override state: State = this.getInitialState();

  getInitialState(): State {
    const {nodeIdToInputSampleIds, virtualNodeGraph} = this.props;
    const rowsByNodeId: RowsByNodeId = {};

    for (const virtualNodeId of virtualNodeGraph.getVirtualNodeIds()) {
      const stageIndex = virtualNodeGraph.getTag(virtualNodeId);
      rowsByNodeId[virtualNodeId] = {
        stageEntry: virtualNodeGraph.originalGraph.getDefaultStageEntry(stageIndex),
        inputSampleIds: (nodeIdToInputSampleIds && nodeIdToInputSampleIds[virtualNodeId]) || [],
      };
    }

    for (const nodeIdToEdit of this.props.nodeIdsToEdit) {
      const stageRun = this.props.virtualNodeGraph.originalGraph.getStageRun(nodeIdToEdit);
      rowsByNodeId[nodeIdToEdit] = {
        stageEntry: stageRun.stageEntry,
        inputSampleIds:
          (nodeIdToInputSampleIds && nodeIdToInputSampleIds[nodeIdToEdit]) || stageRun.inputSampleIds,
        name: stageRun.name,
      };
    }

    return {
      rowsByNodeId,
      virtualNodeGraph,
    };
  }

  _handleClickNode = (nodeId: string): void => {
    if (this.props.nodeIdToSetParent) {
      this.setState({
        virtualNodeGraph: this.state.virtualNodeGraph.setParentNodeIds(
          this.props.nodeIdToSetParent,
          nodeId ? [nodeId] : []
        ),
      });
    }
  };

  _handleCtrlClickNode = (nodeId: string): void => {
    if (this.props.nodeIdToSetParent && nodeId) {
      const existingParentNodeIds = this.state.virtualNodeGraph.getParentNodeIds(
        this.props.nodeIdToSetParent
      );
      const newParentNodeIds = existingParentNodeIds.includes(nodeId)
        ? existingParentNodeIds.filter((parentNodeId: string) => parentNodeId !== nodeId)
        : [...existingParentNodeIds, nodeId];
      this.setState({
        virtualNodeGraph: this.state.virtualNodeGraph.setParentNodeIds(
          this.props.nodeIdToSetParent,
          newParentNodeIds
        ),
      });
    }
  };

  _isNodeClickable = (nodeId: string): boolean => {
    const {nodeIdToSetParent} = this.props;
    const {virtualNodeGraph} = this.state;
    if (!nodeIdToSetParent) {
      return false;
    } else if (virtualNodeGraph.isDescendant(nodeId, nodeIdToSetParent)) {
      return false;
    } else if (virtualNodeGraph.getTag(nodeId) > virtualNodeGraph.getTag(nodeIdToSetParent)) {
      return false;
    } else {
      return true;
    }
  };

  _getButtonText(): string {
    const numNewRuns = this.props.virtualNodeGraph.getVirtualNodeIds().length;
    const numEditedRuns = this.props.nodeIdsToEdit.length;
    if (numNewRuns === 0) {
      if (numEditedRuns === 0) {
        return 'No stage runs to update';
      } else {
        return `Edit ${numEditedRuns} ${benchlingUtil.pluralize(numEditedRuns, 'run')}`;
      }
    } else {
      return `Create ${numNewRuns} ${benchlingUtil.pluralize(numNewRuns, 'run')}`;
    }
  }

  _getButtons(): Array<ModalButton> {
    return [
      {
        text: this._getButtonText(),
        style: ButtonStyle.PRIMARY,
        onClick: this._handleSubmit,
        disabled: this._getCreateSpecifiers().length + this._getEditSpecifiers().length === 0,
        key: 'create',
      },
    ];
  }

  _handleSelectStageEntry(stageEntry: StageEntryDocument, nodeId: string): void {
    this.setState(
      reactUpdate(this.state, {
        rowsByNodeId: {
          [nodeId]: {
            $merge: {
              stageEntry,
            },
          },
        },
      })
    );
  }

  _handleEditName(e: InputChangeEvent, nodeId: string): void {
    this.setState(
      reactUpdate(this.state, {
        rowsByNodeId: {
          [nodeId]: {
            $merge: {
              name: e.target.value,
            },
          },
        },
      })
    );
  }

  _getParentSpecifier = (parentNodeId: string): ParentSpecifier => {
    if (this.state.virtualNodeGraph.isVirtual(parentNodeId)) {
      return {clientStageRunId: parentNodeId};
    } else {
      return {existingStageRunId: parentNodeId};
    }
  };

  _getInputSampleSpecifiers(inputSampleIds: Array<string>): Array<{stageRunSampleId: string}> {
    return inputSampleIds.map((inputSampleId) => ({stageRunSampleId: inputSampleId}));
  }

  _getCreateSpecifiers(): Array<CreateSpecifier> {
    const {virtualNodeGraph} = this.state;
    return virtualNodeGraph.getVirtualNodeIds().map((nodeId: string) => {
      const stageIndex = virtualNodeGraph.getTag(nodeId);
      const workflowStage = this.props.workflowStages!.find(
        (stage) => stage.stageConfiguration.index === stageIndex
      );
      const {stageEntry, inputSampleIds} = this.state.rowsByNodeId[nodeId];
      return {
        clientStageRunId: nodeId,
        inputSampleSpecifiers: this._getInputSampleSpecifiers(inputSampleIds),
        workflowStageId: workflowStage!.id,
        stageEntryId: stageEntry ? stageEntry.id : null,
        experimentalConditionValueIds: [],
        parentSpecifiers: virtualNodeGraph
          .getParentNodeIds(nodeId)
          .map((parentNodeId: string) => this._getParentSpecifier(parentNodeId)),
      };
    });
  }

  /**
   * Return the part of the edit specifier that corresponds to setting the parent.
   */
  _getSetParentPatch(existingNodeId: string): SetParentPatch {
    const parentNodeIds: Array<string> = this.state.virtualNodeGraph.getParentNodeIds(existingNodeId);
    const originalParentNodeIds = this.state.virtualNodeGraph.getOriginalParentNodeIds(existingNodeId);
    if (!isEqual(parentNodeIds, originalParentNodeIds)) {
      return {
        stageRunId: existingNodeId,
        parentSpecifiers: parentNodeIds.map((parentNodeId) => this._getParentSpecifier(parentNodeId)),
      };
    } else {
      return {};
    }
  }

  /**
   * Return the part of the edit specifier that corresponds to the row.
   */
  _getSetRowPatch(existingNodeId: string): SetRowPatch {
    if (this.props.nodeIdsToEdit.includes(existingNodeId)) {
      const {stageEntry, inputSampleIds, name} = this.state.rowsByNodeId[existingNodeId];
      return {
        stageRunId: existingNodeId,
        name,
        inputSampleSpecifiers: this._getInputSampleSpecifiers(inputSampleIds),
        stageEntryId: stageEntry ? stageEntry.id : null,
      };
    } else {
      return {};
    }
  }

  _getEditSpecifiers(): Array<EditSpecifier> {
    return this.state.virtualNodeGraph
      .getOriginalNodeIds()
      .map((nodeId: string) => {
        const setParentPatch = this._getSetParentPatch(nodeId);
        const setRowPatch = this._getSetRowPatch(nodeId);
        return {...setParentPatch, ...setRowPatch};
      })
      .filter((editSpecifier: EditSpecifier) => !isEqual(editSpecifier, {}));
  }

  _handleSubmit = async (): Promise<void> => {
    const numNewRuns = this.props.virtualNodeGraph.getVirtualNodeIds().length;
    const numEditedRuns = this.props.nodeIdsToEdit.length;
    const createSpecifiers = this._getCreateSpecifiers();
    const editSpecifiers = this._getEditSpecifiers();
    await StageRunActions.bulkConfigureStageRuns({createSpecifiers, editSpecifiers});
    this.props.onRequestSubmit(numNewRuns + numEditedRuns);
    this.props.onRequestClose();
  };

  _renderRow = (nodeId: string): JSX.Element => {
    const {workflowStages} = this.props;
    const {virtualNodeGraph} = this.state;
    const stageIndex = virtualNodeGraph.getTag(nodeId);
    const workflowStage = workflowStages!.find((stage) => stage.stageConfiguration.index === stageIndex);
    const isInitialStageRun = virtualNodeGraph.getParentNodeIds(nodeId).length === 0;
    const commonProps = {
      inputSampleIds: this.state.rowsByNodeId[nodeId].inputSampleIds,
      isInitialStageRun,
      key: nodeId,
      stageName: workflowStage!.name,
      stageEntry: this.state.rowsByNodeId[nodeId].stageEntry!,
      stageEntries: workflowStage!.stageEntries,
      stageRunSampleById: this.props.stageRunSampleById,
      onSelectStageEntry: (stageEntry: StageEntryDocument) => {
        this._handleSelectStageEntry(stageEntry, nodeId);
      },
    };
    if (virtualNodeGraph.isVirtual(nodeId)) {
      return <ConfigureStageRunRow isEditingStageRun={false} {...commonProps} />;
    } else {
      return (
        <ConfigureStageRunRow
          isEditingStageRun={true}
          name={this.state.rowsByNodeId[nodeId].name!}
          onEditName={(e) => {
            this._handleEditName(e, nodeId);
          }}
          {...commonProps}
        />
      );
    }
  };

  override render(): JSX.Element {
    const {nodeIdsToEdit, onRequestClose, stageConfigurations} = this.props;
    const {virtualNodeGraph} = this.state;
    const relevantNodeIds = _.sortBy([...nodeIdsToEdit, ...virtualNodeGraph.getVirtualNodeIds()], (nodeId) =>
      virtualNodeGraph.getTag(nodeId)
    );
    return (
      <Modal
        className="createStageRunModal"
        large={true}
        title="Update stage runs"
        onRequestClose={onRequestClose}
        buttons={this._getButtons()}
      >
        <Row>
          <div className="configureParentNodeVisualization-container">
            <AutoSizer disableHeight={true}>
              {({width}) => (
                <VirtualNodeGraphVisualization
                  containerWidth={width}
                  clickableNodeIds={virtualNodeGraph
                    .getNodeIds()
                    .filter((nodeId: string) => this._isNodeClickable(nodeId))}
                  nodeIdsToEdit={nodeIdsToEdit}
                  onClickNode={this._handleClickNode}
                  onCtrlClickNode={this._handleCtrlClickNode}
                  stageConfigurations={stageConfigurations}
                  virtualNodeGraph={virtualNodeGraph}
                />
              )}
            </AutoSizer>
          </div>
        </Row>
        <div className="metadata-table simpleDivTable createStageRunModal-stageRunTable">
          <div className="simpleDivTable-header">
            <ConfigureStageRunHeader text="Stage" />
            <ConfigureStageRunHeader text="Input" />
            <ConfigureStageRunHeader text="Run" />
            <ConfigureStageRunHeader text="Entry" />
          </div>
          {relevantNodeIds.map((nodeId) => this._renderRow(nodeId))}
        </div>
      </Modal>
    );
  }
}

export default connect(mapStateToProps)(ConfigureStageRunsModal);
